import { type Dayjs } from 'dayjs';
import { useCountdown } from '@vueuse/core';

export default ({
  expiryDateTime,
  minutes,
}: {
  expiryDateTime?: string | null;
  minutes: number;
  daysBack?: number;
}) => {
  const { $dayjs } = useNuxtApp();
  const expiryInterval = ref<string>('');
  const now = ref<Dayjs>($dayjs());
  const isDone = ref(false);

  const expirySource = ref<string | null>(expiryDateTime ?? null);

  const effectiveExpiry = () => {
    const candidate = expirySource.value ? $dayjs(expirySource.value) : null;
    return candidate && candidate.isValid()
      ? candidate
      : $dayjs().add(minutes, 'minutes');
  };

  const expiryTime = computed(() => effectiveExpiry());

  const secondsLeft = () =>
    Math.max(0, expiryTime.value.diff($dayjs(), 'seconds'));

  const updateCountdown = () => {
    now.value = $dayjs();
    const left = Math.max(0, expiryTime.value.diff(now.value, 'seconds'));
    expiryInterval.value = $dayjs.duration(left, 'seconds').format('HH:mm:ss');
    if (left <= 0) {
      isDone.value = true;
      stop();
    }
  };

  const { start, stop, pause, resume } = useCountdown(secondsLeft(), {
    onTick() {
      updateCountdown();
    },
  });

  const reset = (newExpiry?: string | null) => {
    if (typeof newExpiry !== 'undefined') {
      expirySource.value = newExpiry;
    }
    isDone.value = false;
    now.value = $dayjs();
    stop();
    updateCountdown();
    start(secondsLeft());
  };

  onMounted(() => {
    const left = secondsLeft();
    expiryInterval.value = $dayjs.duration(left, 'seconds').format('HH:mm:ss');
    if (left > 0) {
      isDone.value = false;
      start(left);
    } else {
      isDone.value = true;
    }
  });

  onBeforeUnmount(() => stop());

  return {
    expiryInterval,
    expiryTime,
    now,
    isDone,
    reset,
    pause,
    resume,
  };
};
